
import type { ChangePasswordFactory, User } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { ErrorMessage, UserRoleManagementMessages } from '../../models'
import { computed, defineComponent, ref } from 'vue'
import { useAppLoading } from '../../composables/use-app-loading'
import { useUsersError } from '../../composables/users/use-users-error'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { UiModal } from '../keen-ui'
import { EpbButton } from '../shared/epb-button'
import { EpbTextbox } from '../shared/epb-textbox'

export default defineComponent({
  name: 'ChangePasswordModal',
  components: {
    EpbButton,
    EpbTextbox,
    UiModal,
  },
  props: {
    user: {
      type: Object as PropType<User>,
      required: true,
    },
    messages: {
      type: Object as PropType<UserRoleManagementMessages['account']['changePassword']>,
      required: true,
    },
  },
  emits: {
    success: (message: string) => {},
    error: (message: ErrorMessage) => {},
  },
  setup(props, { emit }) {
    const factory = CONTAINER.get<ChangePasswordFactory>(TYPES.ChangePasswordFactory)

    const { setLoading } = useAppLoading()
    const { getErrorMessage } = useUsersError(props)

    const baseClass = 'epb-change-password-modal'

    const modal = ref()

    const value = ref(factory())
    const repeatedNewPassword = ref('')

    const valid = computed(
      () => value.value.valid && value.value.newPassword === repeatedNewPassword.value,
    )

    function open(): void {
      value.value = factory()
      repeatedNewPassword.value = ''
      modal.value.open()
    }

    function close(): void {
      modal.value.close()
    }

    async function accept(): Promise<void> {
      setLoading(true)
      try {
        await value.value.save(props.user.id)
        close()
        emit('success', props.messages.success)
      } catch (error) {
        emit('error', getErrorMessage(error))
      } finally {
        setLoading(false)
      }
    }

    return {
      baseClass,
      modal,
      value,
      repeatedNewPassword,
      valid,
      open,
      close,
      accept,
    }
  },
})
